import { AppContext, Features } from "@lightspeed-invoicing/core-client/frontend/index"
import React from "react"
import { FormattedMessage, injectIntl } from "react-intl"
import { ConnectedProps, connect } from "react-redux"
import { ReferenceBadge } from "../../common/badge"
import QuoteItemInventoryRequested from "../../common/quote/iteminventoryrequested.component"
import QuoteItemInventoryReserved from "../../common/quote/iteminventoryreserved.component"
import QuoteItemInvoiced from "../../common/quote/iteminvoiced.component.jsx"
import { STOCK_DOCUMENT_STATUS } from "../../common/types/inventory"
import { ORDER_STATUS_TYPE } from "../../order-status/OrderStatusType"
import DisabledRowBadgeComponent from "../../quote/components/DisabledRowBadgeComponent"
import QuoteItemStatusBadge from "../../quote/itemstatus.component"
import { DOCUMENT_TYPE } from "../../state/Constants"
import IntlProps from "../../state/helpers/IntlProps"
import { StockDocumentAttributes } from "../../state/inventory/stockdocument/models"
import { LocationDto } from "../../state/location/Location"
import { ORDERS } from "../../state/order/Constants"
import { PricingFeeCalculationDto } from "../../state/pricing/PricingFeeCalculation"
import { patchPricingTableItem } from "../../state/pricing/actions"
import RowCustomStatus from "../../state/pricing/rowcustomstatus/RowCustomStatus"
import { PricingTableSelector, getPricingTableItem } from "../../state/pricing/selectors"
import { PRODUCT_TYPE } from "../../state/product/Types"
import { INVENTORY_STOCK_DIRECTION, STOCK_DOCUMENTS } from "../../utils/inventory"
import RowCustomStatusDropdownComponent from "../rowcustomstatus/component/RowCustomStatusDropdownComponent"
import ItemMovementStatus from "./ItemMovementStatus"

type OwnProps = {
    itemId: string
    mainObjectId: string
    showInventoryDetails: boolean
    location: LocationDto
    type: DOCUMENT_TYPE
    allowEdit: boolean
    showStatusDetails?: boolean
    fee: PricingFeeCalculationDto[]
    rowCustomStatuses: RowCustomStatus[]
    stockDocument:StockDocumentAttributes | undefined
    documentStatus: string,
}

const ProductStatus = (props: Props & IntlProps) => {
    const isInventoryEnabled = AppContext.isInventoryEnabled()
    
    const onRowCustomStatusUpdate = (rowCustomStatusID: string) => {
        AppContext.getApiClient().patch(`/${props.type}-items/${props.item?.id}`, {
            rowCustomStatusId: rowCustomStatusID
        })
    }

    const showInventoryStatuses = props.showInventoryDetails && props.item
        && !props.item?.attributes.itemObject.attributes.isNonInventory
        && props.item?.attributes.itemObject.attributes.type !== PRODUCT_TYPE.BUNDLE && !props.stockDocument &&
        AppContext.hasFeatureIntegration(Features.PurchaseOrderTracking)
        || isInventoryEnabled && props.item && props.itemInventoryTracking 
        && [ORDER_STATUS_TYPE.StatusDraft, ORDER_STATUS_TYPE.StatusOrdered].includes(props.documentStatus)

    const showRequestedInventory = AppContext.hasFeatureIntegration(Features.PurchaseOrderTracking)
        || props.item && props.item.attributes.references?.filter(ref => ref.attributes.referenceKey === STOCK_DOCUMENTS).length > 0

    const references = props.item?.attributes.references?.filter(ref => ref.attributes.referenceKey === ORDERS) ?? []

    return (
        <div className={"d-flex flex-wrap align-items-center pl-2 w-100"}>

            {!props.stockDocument && <div className={"w-100 align-items-center d-flex flex-wrap"}>
                {props.item &&
                    <RowCustomStatusDropdownComponent
                        className={"mr-2 p-0 mb-1"}
                        buttonStyle={{fontSize: "0.7rem", maxWidth: "100px", overflow: "hidden"}}
                        selectedRowCustomStatusID={props.item.attributes.customStatusId}
                        rowCustomStatuses={props.rowCustomStatuses}
                        onSelectCallback={onRowCustomStatusUpdate}
                    />
                }

                <QuoteItemStatusBadge
                    className={"align-items-start mr-1 pb-2 mt-0"}
                    status={props.item?.attributes.type}
                />

                {!props.item?.attributes.taxable && !props.item?.attributes.parentId &&
                    <div className="badge badge-info mb-1 text-with-icon mr-1">
                        <FormattedMessage
                            id="quote.item.taxable.badge_exempt_label"
                            defaultMessage="Tax exempt"
                        />
                    </div>
                }

                {props.item?.attributes.hidePrice &&
                    <div className="badge badge-info align-items-center mb-1 text-with-icon mr-1">
                        <FormattedMessage
                            id="quote.item.hide_price.badge_label"
                            defaultMessage="Hidden price"
                        />
                    </div>
                }

                {!props.item?.attributes.feeApplicable && props.fee.length > 0 &&
                    <div className="mt-1 mb-1 mr-1 mb-1 badge badge-info text-with-icon">
                        <FormattedMessage
                            id="quote.item.taxable.fees_exempt_label"
                            defaultMessage="Fee Exempt"
                        />
                    </div>
                }

                {props.item?.attributes.measureEditable &&
                    <div className="mr-1 mb-1 badge badge-info text-with-icon">
                        <FormattedMessage
                            id="quote.item.measure_editable_badge_label"
                            defaultMessage="Quantity editable"
                        />
                    </div>
                }

                {
                    props.item?.attributes.isDisabled &&
                    <DisabledRowBadgeComponent disableReasonKey={props.item.attributes.disableReasonKey}/>
                }
            </div>
            }

            {props.stockDocument && <div className={"w-100 align-items-center d-flex flex-wrap"}>
                {!props.item?.attributes.taxable && !props.item?.attributes.parentId &&
                    <div className="badge badge-info mb-1 text-with-icon mr-1">
                        <FormattedMessage
                            id="quote.item.taxable.badge_exempt_label"
                            defaultMessage="Tax exempt"
                        />
                    </div>
                }
                {!props.item?.attributes.feeApplicable && props.fee.length > 0 &&
                    <div className="mt-1 mb-1 mr-1 mb-1 badge badge-info text-with-icon">
                        <FormattedMessage
                            id="quote.item.taxable.fees_exempt_label"
                            defaultMessage="Fee Exempt"
                        />
                    </div>
                }
            </div>
            }


            {props.showInventoryDetails && !props.item?.attributes.parentId && !props.stockDocument &&
                <QuoteItemInvoiced
                    value={props.item}
                    className={"mb-1"}
                    type={props.type}
                />}

            {showInventoryStatuses &&
                [<QuoteItemInventoryReserved
                    key={"reserved"}
                    title={"Inventory " + props.item?.attributes.activeTitle}
                    editable={props.allowEdit && props.item?.attributes?.invoicedMeasure == 0}
                    type={props.type}
                    itemId={props.itemId}
                    location={props.location}
                    className={"mr-2 mb-1"}
                    mainObjectId={props.mainObjectId}
                    documentStatus={props.documentStatus}
                />,
                showRequestedInventory &&
                    <QuoteItemInventoryRequested
                        className={"mr-2 mb-1"}
                        value={props.item}
                        type={props.type}
                        mainObjectId={props.mainObjectId}
                    />
                ]
            }

            {props.stockDocument && props.stockDocument.status.attributes.state != STOCK_DOCUMENT_STATUS.DRAFT &&
                props.stockDocument.definition.attributes.direction != INVENTORY_STOCK_DIRECTION.IN_OUT &&
                <ItemMovementStatus
                    direction={props.stockDocument.definition.attributes.direction}
                    item={props.item}
                />
            }

            {references.map(ref => <ReferenceBadge key={ref.id} reference={ref}/>)}
        </div>
    )
}

const mapState = (state, props: OwnProps) => {
    const id = props.itemId
    const objectId = props.mainObjectId
    const item = getPricingTableItem(state, id, objectId)
    const itemInventoryTracking = PricingTableSelector.getItemInventoryTracking(state, id)
    return {
        ...props,
        item: item,
        itemInventoryTracking: itemInventoryTracking,
    }
}
const mapDispatch = {patchPricingTableItem}
const connector = connect(mapState, mapDispatch)
type Props = ConnectedProps<typeof connector>
export default connector(injectIntl(ProductStatus))


// WEBPACK FOOTER //
// ./src/pricing/components/ProductStatusComponent.tsx